import { useState, FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../config/firebase';
import { Alert, Button, Input } from '../components/shared';
import logoFull from '../assets/logo_full.png';

export function LoginPage() {
  const { signIn, signInWithGoogle, signInWithMicrosoft, authError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawReturnUrl = searchParams.get('returnUrl') || '/';
  const returnUrl = rawReturnUrl.startsWith('/') && !rawReturnUrl.startsWith('//') ? rawReturnUrl : '/';
  const inviteEmail = searchParams.get('email');
  const fromPricing = searchParams.get('from') === 'pricing' || document.referrer.includes('/pricing');

  const [email, setEmail] = useState(() => inviteEmail || '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(() =>
    authError
      ? 'Your session has expired or you do not have permission to access this resource. Please sign in again.'
      : '',
  );
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<'google' | 'microsoft' | null>(null);
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email, password);
      navigate(inviteEmail ? '/activate-account' : returnUrl);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  function handleOAuthSignIn(provider: 'google' | 'microsoft', signInFn: () => Promise<void>) {
    return async () => {
      setError('');
      setOauthLoading(provider);
      try {
        await signInFn();
        navigate(returnUrl);
      } catch (err: unknown) {
        if (err instanceof Error && err.message.includes('popup-closed-by-user')) return;
        const fallback = `${provider.charAt(0).toUpperCase() + provider.slice(1)} sign-in failed`;
        setError(err instanceof Error ? err.message : fallback);
      } finally {
        setOauthLoading(null);
      }
    };
  }

  const handleGoogleSignIn = handleOAuthSignIn('google', signInWithGoogle);
  const handleMicrosoftSignIn = handleOAuthSignIn('microsoft', signInWithMicrosoft);

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address first.');
      return;
    }
    setError('');
    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send password reset email');
    } finally {
      setResetLoading(false);
    }
  };

  const isDisabled = loading || oauthLoading !== null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-50 px-4">
      <div className="max-w-md w-full space-y-8">
        {/* Logo & Title */}
        <div className="text-center">
          <img src={logoFull} alt="LasoXP" className="h-10 mx-auto" />
          <p className="mt-4 text-sm text-surface-600">
            {inviteEmail
              ? "You've been invited! Sign in to get started."
              : fromPricing
                ? 'Sign in to continue setup. If you need a new account provisioned, contact sales first.'
                : 'Sign in to your account, or create one to get started.'}
          </p>
          {fromPricing && !inviteEmail && (
            <p className="mt-2 text-sm">
              <a
                href="mailto:sales@lasoxp.com?subject=LasoExperience%20Platform%20Access"
                className="text-primary-600 hover:text-primary-700"
              >
                Need a new account? Contact sales.
              </a>
            </p>
          )}
        </div>

        {/* Error message */}
        {error && <Alert variant="danger">{error}</Alert>}

        {/* OAuth Providers */}
        <div className="space-y-3">
          <Button
            type="button"
            variant="secondary"
            fullWidth
            onClick={handleGoogleSignIn}
            disabled={isDisabled}
            icon={
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
            }
          >
            {oauthLoading === 'google' ? 'Signing in...' : 'Continue with Google'}
          </Button>

          <Button
            type="button"
            variant="secondary"
            fullWidth
            onClick={handleMicrosoftSignIn}
            disabled={isDisabled}
            icon={
              <svg className="h-5 w-5" viewBox="0 0 21 21">
                <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
              </svg>
            }
          >
            {oauthLoading === 'microsoft' ? 'Signing in...' : 'Continue with Microsoft'}
          </Button>
        </div>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-surface-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-surface-50 text-surface-500">or continue with email</span>
          </div>
        </div>

        {/* Email/Password Form */}
        <form className="space-y-4" onSubmit={handleSubmit}>
          <Input
            id="email"
            label="Email address"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isDisabled}
            placeholder="admin@example.com"
          />

          <div>
            <Input
              id="password"
              label="Password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isDisabled}
              placeholder="••••••••"
            />
            <div className="mt-1 text-right">
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={handleForgotPassword}
                disabled={resetLoading}
              >
                {resetLoading ? 'Sending...' : 'Forgot password?'}
              </Button>
            </div>
            {resetSent && (
              <p className="mt-1 text-sm text-success-600">
                Password reset email sent. Check your inbox.
              </p>
            )}
          </div>

          <Button
            type="submit"
            variant="primary"
            fullWidth
            size="lg"
            disabled={isDisabled}
            loading={loading}
          >
            {loading ? 'Continuing...' : 'Continue'}
          </Button>
        </form>
      </div>
    </div>
  );
}
